/**
 * usePermission hook
 *
 * Story 5.3: Organization Settings and Management UI (Task 8)
 * Role-based permission inference from TenantContext.
 * No backend call needed - derives permissions from currentOrgRole.
 */

import { useTenant } from './useTenant';

export type PermissionKey =
  | 'canEditOrg'
  | 'canDeleteOrg'
  | 'canManageMembers'
  | 'canChangeRoles'
  | 'canCreateTeam'
  | 'canManageDomains';

export interface Permissions {
  /** Owner or admin can edit organization details */
  canEditOrg: boolean;
  /** Only owner can delete organization */
  canDeleteOrg: boolean;
  /** Owner or admin can manage members (invite, remove) */
  canManageMembers: boolean;
  /** Only owner can change member roles */
  canChangeRoles: boolean;
  /** Owner or admin can create teams */
  canCreateTeam: boolean;
  /** Owner or admin can manage domain governance (mirrors OpenFGA can_manage_domains) */
  canManageDomains: boolean;
}

/**
 * Hook to check user permissions based on organization role.
 *
 * @returns Object with boolean flags for each permission
 *
 * @example
 * const { canDeleteOrg, canEditOrg } = usePermission();
 * if (canDeleteOrg) { showDeleteButton(); }
 */
export const usePermission = (): Permissions => {
  const { currentOrgRole } = useTenant();

  const isOwner = currentOrgRole === 'owner';
  const isAdmin = currentOrgRole === 'admin';
  const isOwnerOrAdmin = isOwner || isAdmin;

  return {
    canEditOrg: isOwnerOrAdmin,
    canDeleteOrg: isOwner,
    canManageMembers: isOwnerOrAdmin,
    canChangeRoles: isOwner,
    canCreateTeam: isOwnerOrAdmin,
    canManageDomains: isOwnerOrAdmin,
  };
};
